import { dehydrate, QueryClient } from '@tanstack/react-query';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import React from 'react';

import { getPageByUrl } from '@/api/pageMaker';
import ChannelTalkBtn from '@/components/common/ChannelTalkBtn';
import Footer from '@/components/common/Footer';
import Floating from '@/components/common/PageMaker/Floating/template';
import GridCardsDivFrame from '@/components/common/PageMaker/GridCardsDivFrame/template';
import ImageWithButtonDivFrame from '@/components/common/PageMaker/ImageWithButtonDivFrame/template';
import JustImageDivFrame from '@/components/common/PageMaker/JustImageDivFrame/template';
import type { Page } from '@/components/common/PageMaker/type';
import { GNB } from '@/components/gnb';
import { SITE_ORIGIN } from '@/constants/seo';
import { BeusableScript } from '@/lib/sdk/beusable';
import { loginRequiredSSR } from '@/lib/utils/businessLogics/loginValidation';

const components = (div) => {
  return {
    justImage: <JustImageDivFrame div={div} />,
    imageWithButton: <ImageWithButtonDivFrame div={div} />,
    gridCards: <GridCardsDivFrame div={div} />,
  };
};

export default function PageMakerPreviewPage({
  urlPath,
  serverSidePageData,
}: {
  urlPath: string[] | string;
  serverSidePageData: { pages: Page };
}) {
  return (
    <>
      <Head>
        <meta name='author' content='스파르타코딩클럽' />
        <meta
          name='description'
          content={
            serverSidePageData.pages.description ||
            '현직 개발자의 생생한 후기들이 증명합니다. 개발자 커리어 개척캠프 항해99. 끈기를 기르고 결국 해내는 개발자로 성장하세요.'
          }
        />
        <meta
          name='keywords'
          content='코딩 부트캠프, 주니어 개발자, 웹개발, 코딩공부, 프로그래밍, 파이썬, 코딩학원, 코딩 스터디, 부트캠프, 개발자, 코딩, 개발자 취업, AWS, 웹서버, 서버, 클라이언트, 자바스크립트, 리액트, React, Spring, 스프링, 앱개발, React Native, 프론트엔드, 백엔드, 풀스택, AI 개발자, LLM, 개발자 이직, 개발자 물경력, 개발자 멘토링, TDD'
        />
        <meta property='og:url' content={`${SITE_ORIGIN}/${urlPath}`} />
        <meta
          property='og:title'
          content={
            serverSidePageData.pages.ogTitle ||
            `${serverSidePageData.pages.name}`
          }
        />
        <meta
          property='og:image'
          content={serverSidePageData.pages.ogImage || `/images/main/og.png`}
        />
        <meta
          property='og:description'
          content={
            serverSidePageData.pages.description ||
            '10년이 지나도 남는 커리큘럼을 바탕으로 커리어를 개척하세요. 진정성있는 멘토링과 2천 명이 넘는 끈끈한 커뮤니티가 여러분과 함께 합니다.'
          }
        />
        <meta property='og:site_name' content='항해99' />
        <meta property='og:type' content='website' />
        <meta property='og:locale' content='ko_KR' />
      </Head>
      <GNB />
      {serverSidePageData.pages.divs.map((div) => (
        <React.Fragment key={div?.id}>
          {components(div)[div.styleName]}
        </React.Fragment>
      ))}
      {serverSidePageData.pages.floating &&
        serverSidePageData.pages.floating.isShowing && (
          <Floating {...serverSidePageData.pages.floating} />
        )}
      <Footer />
      <ChannelTalkBtn bottom={80} />
      <BeusableScript />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { params, req, res, resolvedUrl } = context;
  const urlPath = params?.slug as string;
  const [userInfo] = loginRequiredSSR(req, res, resolvedUrl);

  const pageData = await getPageByUrl(urlPath);

  if (!pageData || !pageData?.pages) {
    return {
      notFound: true,
    };
  }

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: ['getPageByUrl', urlPath],
    queryFn: () => getPageByUrl(urlPath),
  });

  return {
    props: {
      urlPath,
      userInfo,
      dehydratedState: dehydrate(queryClient),
      serverSidePageData: pageData ?? null,
    },
  };
};
